import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import _get from 'lodash-es/get';
import _noop from 'lodash-es/noop';
import Button from 'sushiweb/Button';
import Layout from 'sushiweb/Layout/Layout';
import H5 from 'sushiweb/Typography/Heading/H5';
import { Download } from 'sushiweb/Icons';
import { P } from 'sushiweb/Typography/Text';
import Spinner from 'sushiweb/Spinner/Spinner';
import red from 'sushiweb/tokens/color/red';
import ChevronLeft from 'sushiweb/Icons/all/ChevronLeft';
import Upload from 'sushiweb/Icons/all/Upload';
import {
  fileDataFormat,
  fileDataSize,
  sampleFileDataFormat,
} from '../../../Onboarding/constants';
import { SampleEmployeeTable } from '../../../common/components/DataTables/components/SampleEmployees';
import {
  ConfirmUpload,
  Footer,
  FooterWrapper,
  FormWrapper,
  RightButton,
} from '../../../Onboarding/components/styles';
import { Container } from '../../../Onboarding/styles';
import { UPLOAD_GROUP_URL } from '../../../../constants/urls';
import DownloadComponent from '../../../common/components/DownloadComponent/DownloadComponent';
import Heading from '../../../Onboarding/components/Heading';
import { GroupDetails } from '../../styles';
import { InputWrapper, StyledInput } from '../../../WalletPrograms/styles';
import { infoLabels } from '../../../WalletPrograms/constants';
import usePolling from '../../../common/utils/usePolling';
import { UploadsContainer } from '../../../UploadStatus/styles';
import {
  InstructionText,
  SpinnerHolder,
  StyledLayout,
} from '../../../common/styles';
import {
  wrongFormat,
  uploadSuccessStatus,
  groupUploadMsg,
  genericErrorMsg,
} from '../../../common/components/constants';
import {
  checkFileTypeSize,
  clearInputValue,
  downloadGroupCSV,
} from '../../../../helpers';

const CreateGroups = ({
  groupData,
  processId,
  createGroup,
  showErrorToast,
  creatingGroup,
  groupCreated,
  getGroupDetails,
  clearCreateGroup,
  fetchingGroupDetails,
  setFileUploadMsg,
  uploadMsg,
}) => {
  let groupName = '';
  const navigate = useNavigate();
  const location = useLocation();
  const { id = '' } = useParams();
  const [name, setGroupName] = useState('');
  const [file, setFile] = useState(null);
  const [inputDisabled, setInputDisabled] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploadData, setUploadData] = useState([]);
  const [isPollingStarted, setIsPollingStarted] = useState(false);
  const [pollingFrequency, setPollingFrequency] = useState(2);
  const [groupNameDisabled, setGroupNameDisabled] = useState(false);
  const url = UPLOAD_GROUP_URL;
  const showProgressBar = uploadStatus === '' || uploadStatus === 'completed';

  if (id) {
    groupName = _get(groupData, 'group_name', '');
  }

  useEffect(() => {
    return () => {
      clearCreateGroup();
    };
  }, [location.pathname]);

  useEffect(() => {
    if (groupCreated) {
      setIsPollingStarted(true);
      setInputDisabled(true);
    }
  }, [groupCreated]);

  useEffect(() => {
    if (uploadStatus === 'completed') {
      setIsPollingStarted(false);
    }
  }, [uploadStatus]);

  const TopScroll = () => {
    const successRef = useRef();
    useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); });
    return <div ref={successRef} />;
  };

  const onResponse = ({ isError, response }) => {
    let message = '';
    const status = _get(response, 'data.status', '');
    const uploads = _get(response, 'data.uploads', []);
    setUploadStatus(status);
    setUploadData(uploads);
    if (uploadSuccessStatus.includes(status)) {
      message = _get(response, 'data.message.title', groupUploadMsg);
    } else {
      message = _get(response, 'data.message.title', genericErrorMsg);
    }

    if (isError) {
      setIsPollingStarted(false);
      setFileUploadMsg(groupUploadMsg);
      return;
    }
    setFileUploadMsg(message);
    if (uploadSuccessStatus.includes(status) && !isError) {
      setPollingFrequency(3);
    } else {
      setIsPollingStarted(false);
    }
  };

  useEffect(() => {
    if (id) {
      getGroupDetails(id);
    }
  }, []);

  useEffect(() => {
    if (groupName) {
      setGroupName(groupName);
      setGroupNameDisabled(true);
    }
  }, [groupName]);

  usePolling({
    processId,
    frequency: pollingFrequency,
    isPollingStarted,
    onResponse,
    url,
  });

  const onNameChange = e => {
    if (e.length <= 50 && !groupName) {
      setGroupName(e);
    }
  };

  const onChange = e => {
    checkFileTypeSize(e.target.files[0])
      .then(success => {
        if (success) {
          setFile(e.target.files[0]);
        } else {
          showErrorToast(wrongFormat);
          clearInputValue();
        }
      })
      .catch(err => {
        clearInputValue();
        showErrorToast();
        console.log(err);
      });
  };

  const goBack = () => {
    navigate(-1);
  };

  const goNext = () => {
    const fileBody = new FormData();
    if (groupName) {
      fileBody.append('group_id', id);
      fileBody.append('file', file);
    } else {
      fileBody.append('name', name);
      fileBody.append('file', file);
    }
    createGroup(fileBody);
  };

  const disabled = !!name && file !== null;

  return fetchingGroupDetails && Boolean(id) ? (
    <Container>
      <Container.Content>
        <SpinnerHolder className='center'>
          <Spinner color={red.z_red} size='medium' />
        </SpinnerHolder>
      </Container.Content>
    </Container>
  ) : (
    <>
      <Container>
        <Container.Content>
          {uploadStatus === 'completed' && !isPollingStarted ? (
            <Heading
              heading='Employee Data Upload Completed'
              subheading='Please check the status of the upload below.'
            />
          ) : (
            <Heading
              heading={
                groupName ? 'Add Employees' : 'Create New Employee Group'
              }
              subheading={
                groupName
                  ? 'You can add employees to this group'
                  : 'You can create new group of employees here'
              }
            />
          )}
          {(!showProgressBar || isPollingStarted || creatingGroup) && (
            <TopScroll />
          )}
          <ConfirmUpload
            status={uploadStatus}
            progress={isPollingStarted}
            show={!showProgressBar || isPollingStarted || creatingGroup}
          >
            {uploadMsg}
          </ConfirmUpload>
          {!['failed', 'completed', 'terminated'].includes(uploadStatus) && (
            <GroupDetails>
              <H5>Group Details</H5>
              <InputWrapper>
                <P className='required-field'>Group Name</P>
                <StyledInput
                  label='Name'
                  value={name}
                  onChange={onNameChange}
                  showHelperText={false}
                  disabled={inputDisabled || groupNameDisabled}
                />
                <InputWrapper.HelperText>
                  {infoLabels.group_name}
                </InputWrapper.HelperText>
              </InputWrapper>
            </GroupDetails>
          )}
          {!['failed', 'completed', 'terminated'].includes(uploadStatus) && (
            <FormWrapper>
              <H5>Upload Employee List</H5>
              <SampleEmployeeTable group />
              <P className='required-field'>Upload employee list</P>
              <StyledLayout>
                <Layout.Item colD={6} colT={6} colM={6}>
                  <input
                    id='upload-file'
                    type='file'
                    accept='.csv'
                    onChange={onChange}
                    disabled={inputDisabled}
                  />
                  <InstructionText>
                    <P>Upload format:</P>
                    {' '}
                    {fileDataFormat}
                    {' '}
                    |
                    {' '}
                    <P>File size:</P>
                    {' '}
                    {fileDataSize}
                  </InstructionText>
                </Layout.Item>
                <Layout.Item colD={6} colT={6} colM={6}>
                  <Button
                    onClick={downloadGroupCSV}
                    icon={<Download />}
                    iconAlign='left'
                    size='large'
                  >
                    Download Sample File
                  </Button>
                  <InstructionText>{sampleFileDataFormat}</InstructionText>
                </Layout.Item>
              </StyledLayout>
            </FormWrapper>
          )}
          {uploadStatus === 'completed' && !isPollingStarted && (
            <UploadsContainer>
              {uploadData.map(uploadInfo => {
                const { title = '', subtitle = '', link = '' } = uploadInfo;
                return (
                  <DownloadComponent
                    title={title}
                    label={subtitle}
                    link={link}
                  />
                );
              })}
            </UploadsContainer>
          )}
        </Container.Content>
      </Container>
      <FooterWrapper>
        <Footer>
          <Button
            appearance='outline'
            icon={<ChevronLeft />}
            iconAlign='left'
            btnColor='red'
            onClick={goBack}
            disabled={isPollingStarted || creatingGroup}
          >
            {!['failed', 'completed', 'terminated'].includes(uploadStatus)
              ? 'Go Back'
              : 'Done'}
          </Button>
          {uploadStatus === '' && (
            <RightButton
              iconAlign='right'
              icon={<Upload />}
              btnColor='red'
              onClick={goNext}
              disabled={!disabled}
              loading={creatingGroup || isPollingStarted}
            >
              Upload
            </RightButton>
          )}
        </Footer>
      </FooterWrapper>
    </>
  );
};

CreateGroups.propTypes = {
  processId: PropTypes.string,
  fetchingGroupDetails: PropTypes.bool,
  clearCreateGroup: PropTypes.func,
  createGroup: PropTypes.func,
  creatingGroup: PropTypes.bool,
  groupCreated: PropTypes.bool,
  showErrorToast: PropTypes.func,
  getGroupDetails: PropTypes.func,
  setFileUploadMsg: PropTypes.func,
  groupData: PropTypes.objectOf(PropTypes.any),
  uploadMsg: PropTypes.string,
};

CreateGroups.defaultProps = {
  groupData: {},
  processId: '',
  fetchingGroupDetails: true,
  uploadMsg: '',
  clearCreateGroup: _noop,
  createGroup: _noop,
  getGroupDetails: _noop,
  creatingGroup: false,
  groupCreated: false,
  showErrorToast: _noop,
  setFileUploadMsg: _noop,
};

export default CreateGroups;
